<template lang="pug">
  div(:class="[$style.formItem,'cms-form pt-0 md:pt-ds-md']")
    div(:class="['w-full md:w-6/12 px-ds-md md:pr-ds-sm mt-ds-md md:mt-0']")
      a(
        class="text-white hover:text-white"
        :href="extractHref(ctaFormButtons[0].innerHtml)"
        style="text-decoration: none;")
        Btn(
          :wp-block="ctaFormButtons[0]"
          kind="rebrand-cta"
          size="xl"
          btn-with-padding
          :block="true")
          .text-xl {{ getInnerText(ctaFormButtons[0].innerHtml) }}

    div(:class="['w-full md:w-6/12 px-ds-md md:pl-ds-sm mt-ds-md md:mt-0']")
      template(v-if="ctaFormHasMultipleButtons")
        a(
          class="text-white hover:text-white"
          :href="extractHref(ctaFormButtons[1].innerHtml)"
          style="text-decoration: none;")
          Btn(
            class="ml-0 text-black70 border border-black20 hover:bg-black10"
            kind="rebrand-cta"
            size="xl"
            btn-with-padding
            :block="true")
            .text-xl {{ getInnerText(ctaFormButtons[1].innerHtml) }}
      template(v-else)
        PhoneNumber(
          class="call-us-container call-us-cta flex justify-center align-center w-full h-ds-xxl text-xl text-black70"
          :showIcon="true"
          :inheritColor="true"
          :showButtonText="true"
          :handleClick="() => handleCallUsClick('cta_form_button')"
          :buttonText="getPhoneNumberValue.formatted")
        div(class="call-us-description lg:hidden") {{ $t('location_form.tap_to_call') }}

    div(:class="['flex w-full flex-col-reverse md:flex-row p-ds-md', $i18n.locale === 'en' ? 'justify-between' : 'justify-end']")
      FormFooterUK(
        v-if="$i18n.locale === 'en'"
        :trustpilot-customers="formattedTrustpilotCustomers"
        :trustpilot-rating="formattedTrustpilotRating")
      div(class="hidden md:block")
        button(
          type="button"
          @click.prevent="onEmailFormOpen"
          class="av-button-link text-anyvan-cta-secondary"
        ) {{ $t("location_form.already_received_quote") }}
    FormFooter(
      v-if="$i18n.locale === 'es'"
      :block="block"
      :trustpilot-rating="formattedTrustpilotRating"
    )
    teleport(to="body")
      RetrieveQuoteModal(
        v-if="emailFormShow"
        @close-modal="onEmailFormClose")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapGetters } from "vuex"
import axios from "axios"
import { formatNumberWithLocale } from "~/utils/formatNumberWithLocale"
import { GET_PHONE_NUMBER_VALUE } from "~/store/phone-number"
import { extractHref, getInnerText } from "~/scripts/useful-functions"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  emailFormShow: false,
  trustpilotCustomers: string
  trustpilotRating: number
}

export default defineComponent({
  name: "CtaForm",

  props: {
    block: { type: Object as () => Block, required: true }
  },

  data(): ComponentData {
    return {
      emailFormShow: false,
      trustpilotCustomers: "0",
      trustpilotRating: 4.5,
    }
  },
  computed: {
    ...mapGetters("phone-number", {
      getPhoneNumberValue: GET_PHONE_NUMBER_VALUE
    }),
    ctaBlock(): any {
      return this.block.innerBlocks[this.block.innerBlocks.length - 1]
    },
    ctaFormButtons(): any[] {
      return this.block.innerBlocks[0].innerBlocks[0].innerBlocks[0].innerBlocks
    },
    ctaFormHasMultipleButtons(): boolean {
      return this.ctaFormButtons.length > 1
    },
    formattedTrustpilotCustomers(): string {
      return formatNumberWithLocale(this.trustpilotCustomers, this.$i18n.locale)
    },
    formattedTrustpilotRating(): string {
      return formatNumberWithLocale(this.trustpilotRating, this.$i18n.locale)
    },
    locale(): string {
      return (this.$i18n && this.$i18n.locale) || 'en'
    },
  },


  async created() {
    const trustPilotConfig = this.$config.public.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotAPIKey = trustPilotConfig.apiKey

    if (this.$i18n.locale !== LOCALE.UK) {
      const upperCaseLocale = this.$i18n.locale.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }
    const trustpilotURL = `https://api.trustpilot.com/v1/business-units/${trustPilotId}`
    try {
      const axiosInstance = axios.create() as any
      const response = await axiosInstance.get(trustpilotURL, {
        params: {
          apikey: trustPilotAPIKey
        }
      })
      if (response.status === 200 && response.data) {
        this.trustpilotCustomers = response.data.numberOfReviews.total
        this.trustpilotRating = response.data.score.trustScore
      }
    } catch (err) {
      console.log("Trustpilot error", err)
    }
  },
  methods: {
    extractHref,
    getInnerText,
    onEmailFormOpen() {
      this.emailFormShow = true
    },
    onEmailFormClose() {
      this.emailFormShow = false
    },
    handleCallUsClick(label: string): void {
      this.$snowplow.trackCallUs({
        phoneNumber: this.getPhoneNumberValue.baseRaw,
        label,
        action: "click",
        extension: this.getPhoneNumberValue.extension
      })
    }
  }
})
</script>

<style lang="scss" module>
.formItem {
  @apply relative flex flex-wrap mt-ds-ml bg-white;
  border-radius: 6px;
  box-shadow:
    0 2px 6px 2px rgba(0, 0, 0, 0.15),
    0 1px 2px 0 rgba(0, 0, 0, 0.3);

  :global {
    .call-us-description {
      @apply text-sm text-center italic mb-ds-xxxs;
    }

    .call-us-container {
      transition: 0.2s;
      &:hover {
        filter: saturate(1.5) brightness(0.9);
      }
      a {
        @apply flex justify-center items-center w-full h-ds-xxl px-ds-md py-ds-ms font-bold text-xl text-white no-underline bg-anyvan-cta-secondary border border-black/20;
        border-radius: 8px;
        line-height: normal;

        svg {
          @apply mr-ds-sm;
        }
      }
    }

  }
}
</style>
